import React, { useState, useEffect } from 'react'
import { List, Avatar, Button, Input, Form, Space, Typography, message, Popconfirm, Divider, ConfigProvider, Upload, Image } from 'antd'
import { UserOutlined, LikeOutlined, LikeFilled, DeleteOutlined, EditOutlined, SendOutlined, SmileOutlined, UploadOutlined } from '@ant-design/icons'
import { Comment, Reply } from '../types/course'
import { useAuth } from '../contexts/AuthContext'
import { addComment, getComments, updateComment, deleteComment, likeComment, addReply, likeReply, updateReply, deleteReply } from '../utils/commentUtils'
import { useTranslation } from 'react-i18next'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage, firebaseApp } from '../config/firebaseConfig'
import { getFunctions, httpsCallable } from 'firebase/functions'

const { Text, Paragraph, Title } = Typography
const { TextArea } = Input

interface CommentSectionProps {
  courseId: string
  chapterId: string
}

const CommentSection: React.FC<CommentSectionProps> = ({ courseId, chapterId }) => {
  const { user, userProfile } = useAuth()
  const { t } = useTranslation('onlineCourse')

  const customizeRenderEmpty = () => (
    <div style={{ textAlign: 'center' }}>
      <SmileOutlined style={{ fontSize: 20 }} />
      <p>{t('comments.noComments')}</p>
    </div>
  )
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [editingComment, setEditingComment] = useState<string | null>(null)
  const [editingReply, setEditingReply] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [commentImage, setCommentImage] = useState<File | null>(null)
  const [replyImage, setReplyImage] = useState<File | null>(null)
  const [submittingComment, setSubmittingComment] = useState(false)
  const [submittingReply, setSubmittingReply] = useState(false)

  useEffect(() => {
    loadComments()
  }, [courseId, chapterId])

  const loadComments = async () => {
    setLoading(true)
    try {
      const fetchedComments = await getComments(courseId, chapterId)
      setComments(fetchedComments)
    } catch (error) {
      console.error('Error loading comments:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      message.error(`Failed to load comments: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const uploadImage = async (file: File): Promise<string | null> => {
    if (file.size > 3 * 1024 * 1024) { // 3MB limit
      message.error(t('comments.messages.imageTooLarge'))
      return null
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      message.error(t('comments.messages.invalidImageType'))
      return null
    }

    try {
      const timestamp = Date.now()
      const fileName = `${timestamp}_${file.name}`
      const storageRef = ref(storage, `comment-images/${courseId}/${chapterId}/${fileName}`)
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)
      return downloadURL
    } catch (error) {
      console.error('Error uploading image:', error)
      message.error(t('comments.messages.imageUploadFailed'))
      return null
    }
  }

  const handleAddComment = async () => {
    if (!user) return

    // Check if comment and image are both empty
    if (!newComment.trim() && !commentImage) {
      message.error(t('comments.messages.commentEmpty'))
      return
    }

    // Use display name from profile, or email prefix if not available
    const displayName = userProfile?.displayName || (user.email ? user.email.split('@')[0] : 'Anonymous')
    
    setSubmittingComment(true)
    let photoURL: string | undefined = undefined
    if (commentImage) {
      const uploadedURL = await uploadImage(commentImage)
      if (!uploadedURL) {
        setSubmittingComment(false)
        return // Upload failed
      }
      photoURL = uploadedURL
    }
    
    try {
      await addComment(courseId, chapterId, user.uid, displayName, user.email || '', newComment.trim(), photoURL, userProfile?.avatar)
      setNewComment('')
      setCommentImage(null)
      loadComments()
      message.success(t('comments.messages.commentAdded'))

      // Send email notification to admin
      try {
        const functions = getFunctions(firebaseApp, 'europe-west1')
        const sendEmailFunc = httpsCallable(functions, 'sendEmailFunc')
        const emailData = {
          email: 'ilia@viridian.gmbh',
          subject: 'New Comment on Colozoo Course',
          message: `A new comment has been posted on the course.\n\n
          User: ${displayName}\nEmail: ${user.email}\nCourse ID: ${courseId}\n
          Chapter ID: ${chapterId}\n\nComment:\n${newComment.trim()}`
        }
        await sendEmailFunc(JSON.stringify(emailData))
      } catch (emailError) {
        console.error('Error sending admin notification email:', emailError)
        // Don't show error to user as the comment was successfully added
      }
    } catch (error) {
      console.error('Error adding comment:', error)
      message.error(t('comments.messages.commentAddFailed'))
      // Clear image on error so user can try again
      setCommentImage(null)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return

    try {
      await updateComment(commentId, editText.trim())
      setEditingComment(null)
      setEditText('')
      loadComments()
      message.success(t('comments.messages.commentUpdated'))
    } catch (error) {
      console.error('Error updating comment:', error)
      message.error(t('comments.messages.commentUpdateFailed'))
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId)
      loadComments()
      message.success(t('comments.messages.commentDeleted'))
    } catch (error) {
      console.error('Error deleting comment:', error)
      message.error(t('comments.messages.commentDeleteFailed'))
    }
  }

  const handleLikeComment = async (commentId: string) => {
    if (!user) return

    try {
      await likeComment(commentId, user.uid)
      loadComments()
    } catch (error) {
      console.error('Error liking comment:', error)
      message.error(t('comments.messages.commentLikeFailed'))
    }
  }

  const handleAddReply = async (commentId: string) => {
    if (!user) return

    // Check if reply and image are both empty
    if (!replyText.trim() && !replyImage) {
      message.error(t('comments.messages.replyEmpty'))
      return
    }

    // Use display name from profile, or email prefix if not available
    const displayName = userProfile?.displayName || (user.email ? user.email.split('@')[0] : 'Anonymous')
    
    setSubmittingReply(true)
    let photoURL: string | undefined = undefined
    if (replyImage) {
      const uploadedURL = await uploadImage(replyImage)
      if (!uploadedURL) {
        setSubmittingReply(false)
        return // Upload failed
      }
      photoURL = uploadedURL
    }
    
    try {
      await addReply(commentId, user.uid, displayName, user.email || '', replyText.trim(), photoURL, userProfile?.avatar)
      setReplyingTo(null)
      setReplyText('')
      setReplyImage(null)
      loadComments()
      message.success(t('comments.messages.replyAdded'))
    } catch (error) {
      console.error('Error adding reply:', error)
      message.error(t('comments.messages.replyAddFailed'))
    } finally {
      setSubmittingReply(false)
    }
  }

  const handleEditReply = async (commentId: string, replyId: string) => {
    if (!editText.trim()) return

    try {
      await updateReply(commentId, replyId, editText.trim())
      setEditingReply(null)
      setEditText('')
      loadComments()
      message.success(t('comments.messages.replyUpdated'))
    } catch (error) {
      console.error('Error updating reply:', error)
      message.error(t('comments.messages.replyUpdateFailed'))
    }
  }

  const handleDeleteReply = async (commentId: string, replyId: string) => {
    try {
      await deleteReply(commentId, replyId)
      loadComments()
      message.success(t('comments.messages.replyDeleted'))
    } catch (error) {
      console.error('Error deleting reply:', error)
      message.error(t('comments.messages.replyDeleteFailed'))
    }
  }

  const handleLikeReply = async (commentId: string, replyId: string) => {
    if (!user) return

    try {
      await likeReply(commentId, replyId, user.uid)
      loadComments()
    } catch (error) {
      console.error('Error liking reply:', error)
      message.error(t('comments.messages.replyLikeFailed'))
    }
  }

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date()
    const diff = now.getTime() - timestamp.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return t('comments.time.justNow')
    if (minutes < 60) return t('comments.time.minutes', { count: minutes })
    if (hours < 24) return t('comments.time.hours', { count: hours })
    return t('comments.time.days', { count: days })
  }

  return (
    <>
      <Title level={4} style={{ marginBottom: 20, color: '#333' }}>{t('comments.title')}</Title>
      {/* Add Comment Form */}
      {user && (
        <div style={{ marginBottom: 24 }}>
          <Form layout="vertical">
            <Form.Item>
              <TextArea
                placeholder={String(t('comments.writeComment'))}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Upload
                    key={commentImage ? 'has-image' : 'no-image'}
                    beforeUpload={(file) => {
                      setCommentImage(file)
                      return false // Prevent auto upload
                    }}
                    onRemove={() => setCommentImage(null)}
                    maxCount={1}
                    accept="image/*"
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>
                      {t('comments.attachImage')}
                    </Button>
                  </Upload>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleAddComment}
                    loading={submittingComment}
                    style={{ backgroundColor: '#279063', borderColor: '#279063' }}
                  >
                    {t('comments.commentButton')}
                  </Button>
                </Space>
              </Form.Item>
              {commentImage && (
                <div style={{ marginTop: 8, marginBottom: 16 }}>
                  <Space>
                    <Text type="secondary">{commentImage.name} ({(commentImage.size / 1024 / 1024).toFixed(2)} MB)</Text>
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<DeleteOutlined />} 
                      onClick={() => setCommentImage(null)}
                      style={{ color: '#ff4d4f' }}
                    >
                      Remove
                    </Button>
                  </Space>
                </div>
              )}
            </div>
          </Form>
        </div>
      )}

      {!user && (
        <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
          Please {t('comments.loginRequired')}
        </Text>
      )}

      {/* Comments List */}
      <ConfigProvider renderEmpty={customizeRenderEmpty}>
        <List
          loading={loading}
          dataSource={comments}
          renderItem={(comment) => (
            <List.Item style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ width: '100%' }}>
                {/* Comment Header */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <Avatar 
                    src={comment.userAvatar} 
                    icon={<UserOutlined />} 
                    size="small" 
                    style={{ marginRight: 8 }} 
                  />
                  <Text strong>{comment.userDisplayName}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {formatTimestamp(comment.timestamp)}
                  </Text>
                </div>

                {/* Comment Content */}
                {editingComment === comment.id ? (
                  <div style={{ marginBottom: 8 }}>
                    <TextArea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <Space style={{ marginTop: 8 }}>
                      <Button size="small" type="primary" onClick={() => handleEditComment(comment.id)}>
                        {t('comments.save')}
                      </Button>
                      <Button size="small" onClick={() => setEditingComment(null)}>
                        {t('comments.cancel')}
                      </Button>
                    </Space>
                  </div>
                ) : (
                  <div style={{ marginBottom: 8 }}>
                    <Paragraph>{comment.comment}</Paragraph>
                    {comment.photo && (
                      <Image
                        src={comment.photo}
                        alt="Comment attachment"
                        style={{ maxWidth: 200, maxHeight: 200, marginTop: 8 }}
                        placeholder={<div style={{ width: 200, height: 200, background: '#f0f0f0' }} />}
                      />
                    )}
                  </div>
                )}

                {/* Comment Actions */}
                <Space size="small">
                  <Button
                    type="text"
                    size="small"
                    icon={comment.likedBy.includes(user?.uid || '') ? <LikeFilled /> : <LikeOutlined />}
                    onClick={() => handleLikeComment(comment.id)}
                    disabled={!user}
                  >
                    {comment.likes}
                  </Button>

                  {user && (
                    <>
                      <Button
                        type="text"
                        size="small"
                        onClick={() => setReplyingTo(comment.id)}
                      >
                        {t('comments.replyButton')}
                      </Button>

                      {comment.userId === user.uid && (
                        <>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditingComment(comment.id)
                              setEditText(comment.comment)
                            }}
                          />
                          <Popconfirm
                            title={t('comments.deleteComment')}
                            onConfirm={() => handleDeleteComment(comment.id)}
                            okText={t('comments.yes')}
                            cancelText={t('comments.no')}
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        </>
                      )}
                    </>
                  )}
                </Space>

                {/* Reply Form */}
                {replyingTo === comment.id && user && (
                  <div style={{ marginTop: 12, marginLeft: 32 }}>
                    <Form layout="vertical">
                      <Form.Item>
                        <TextArea
                          placeholder={String(t('comments.writeReply'))}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                        />
                      </Form.Item>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Space>
                            <Upload
                              key={replyImage ? 'has-reply-image' : 'no-reply-image'}
                              beforeUpload={(file) => {
                                setReplyImage(file)
                                return false
                              }}
                              onRemove={() => setReplyImage(null)}
                              maxCount={1}
                              accept="image/*"
                              showUploadList={false}
                            >
                              <Button size="small" icon={<UploadOutlined />}>
                                {t('comments.attachImage')}
                              </Button>
                            </Upload>
                            <Button size="small" type="primary" onClick={() => handleAddReply(comment.id)} disabled={!replyText.trim() && !replyImage} loading={submittingReply}>
                              {t('comments.replyButton')}
                            </Button>
                            <Button size="small" onClick={() => { setReplyingTo(null); setReplyImage(null); setReplyText('') }}>
                              {t('comments.cancel')}
                            </Button>
                          </Space>
                        </Form.Item>
                        {replyImage && (
                          <div style={{ marginTop: 8, marginBottom: 16, marginLeft: 32 }}>
                            <Space>
                              <Text type="secondary" style={{ fontSize: '0.8em' }}>{replyImage.name} ({(replyImage.size / 1024 / 1024).toFixed(2)} MB)</Text>
                              <Button 
                                type="text" 
                                size="small" 
                                icon={<DeleteOutlined />} 
                                onClick={() => setReplyImage(null)}
                                style={{ color: '#ff4d4f' }}
                              >
                                Remove
                              </Button>
                            </Space>
                          </div>
                        )}
                      </div>
                    </Form>
                  </div>
                )}

                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div style={{ marginLeft: 32, marginTop: 12 }}>
                    <Divider style={{ margin: '8px 0' }} />
                    <List
                      dataSource={comment.replies}
                      renderItem={(reply) => (
                        <List.Item style={{ padding: '8px 0', border: 'none' }}>
                          <div style={{ width: '100%' }}>
                            {/* Reply Header */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                              <Avatar 
                                src={reply.userAvatar} 
                                icon={<UserOutlined />} 
                                size="small" 
                                style={{ marginRight: 8 }} 
                              />
                              <Text strong style={{ fontSize: '0.9em' }}>{reply.userDisplayName}</Text>
                              <Text type="secondary" style={{ marginLeft: 8, fontSize: '0.8em' }}>
                                {formatTimestamp(reply.timestamp)}
                              </Text>
                            </div>

                            {/* Reply Content */}
                            {editingReply === reply.id ? (
                              <div style={{ marginBottom: 8 }}>
                                <TextArea
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  autoSize={{ minRows: 1, maxRows: 3 }}
                                />
                                <Space style={{ marginTop: 8 }}>
                                  <Button size="small" type="primary" onClick={() => handleEditReply(comment.id, reply.id)}>
                                    {t('comments.save')}
                                  </Button>
                                  <Button size="small" onClick={() => setEditingReply(null)}>
                                    {t('comments.cancel')}
                                  </Button>
                                </Space>
                              </div>
                            ) : (
                              <div style={{ marginBottom: 4 }}>
                                <Paragraph style={{ fontSize: '0.9em' }}>{reply.comment}</Paragraph>
                                {reply.photo && (
                                  <Image
                                    src={reply.photo}
                                    alt="Reply attachment"
                                    style={{ maxWidth: 150, maxHeight: 150, marginTop: 4 }}
                                    placeholder={<div style={{ width: 150, height: 150, background: '#f0f0f0' }} />}
                                  />
                                )}
                              </div>
                            )}

                            {/* Reply Actions */}
                            <Space size="small">
                              <Button
                                type="text"
                                size="small"
                                icon={reply.likedBy.includes(user?.uid || '') ? <LikeFilled /> : <LikeOutlined />}
                                onClick={() => handleLikeReply(comment.id, reply.id)}
                                disabled={!user}
                              >
                                {reply.likes}
                              </Button>

                              {user && reply.userId === user.uid && (
                                <>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setEditingReply(reply.id)
                                      setEditText(reply.comment)
                                    }}
                                  />
                                  <Popconfirm
                                    title={t('comments.deleteReply')}
                                    onConfirm={() => handleDeleteReply(comment.id, reply.id)}
                                    okText={t('comments.yes')}
                                    cancelText={t('comments.no')}
                                  >
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                    />
                                  </Popconfirm>
                                </>
                              )}
                            </Space>
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
      </ConfigProvider>
    </>
  )
}

export default CommentSection